import React, { useEffect } from 'react';

const EmbedDemo: React.FC = () => {
  useEffect(() => {
    // Create and inject the chat embed script
    const script = document.createElement('script');
    script.src = '/assets/chat-embed.js';
    script.async = true;
    script.onload = () => {
      console.log('Chat embed script loaded successfully');
    };
    script.onerror = () => {
      console.error('Failed to load chat embed script');
    };
    document.head.appendChild(script);

    // Add timeout to hide hint
    const hintTimeout = setTimeout(() => {
      const hint = document.getElementById('chat-hint');
      if (hint) {
        hint.style.display = 'none';
      }
    }, 5000);

    return () => {
      // Cleanup
      clearTimeout(hintTimeout);
      const existingScript = document.querySelector('script[src="/chat-embed.js"]');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }

      // Remove chat elements if they exist
      const chatIcon = document.getElementById('blinqx-chat-icon');
      const chatOverlay = document.getElementById('blinqx-chat-overlay');
      if (chatIcon) chatIcon.remove();
      if (chatOverlay) chatOverlay.remove();
    };
  }, []);

  return (
    <div style={{
      fontFamily: "'Segoe UI', 'Segoe UI Web (West European)', 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif",
      background: 'radial-gradient(109.81% 107.82% at 100.1% 90.19%, #ffcd00 33.63%, #ffe45e 100%)',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#000000',
      padding: '20px'
    }}>
      <div style={{
        textAlign: 'center',
        maxWidth: '800px',
        width: '100%'
      }}>
        <h1 style={{
          fontSize: '3rem',
          marginBottom: '1rem',
          textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
          animation: 'fadeInUp 1s ease-out',
          color: '#000000'
        }}>
          🚀 Quinn Embed Demo
        </h1>

        <p style={{
          fontSize: '1.2rem',
          marginBottom: '2rem',
          color: '#242424'
        }}>
          Experience the power of embeddable chat functionality
        </p>

        <div style={{
          background: 'rgba(255, 255, 255, 0.9)',
          borderRadius: '8px',
          padding: '2rem',
          margin: '2rem 0',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)'
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            marginBottom: '1rem',
            color: '#000000'
          }}>
            📋 How to Test
          </h2>
          <p style={{
            fontSize: '1rem',
            lineHeight: '1.6',
            marginBottom: '1rem',
            color: '#242424'
          }}>
            Look for the chat icon (💬) in the bottom-right corner of your screen. You can:
          </p>
          <ul style={{
            textAlign: 'left',
            paddingLeft: '2rem',
            marginTop: '1rem',
            color: '#242424'
          }}>
            <li>🖱️ <strong>Click</strong> the icon to open the chat popup</li>
            <li>🖐️ <strong>Drag</strong> the icon to move it anywhere on the page</li>
            <li>❌ <strong>Close</strong> the popup using the X button or by clicking outside</li>
            <li>⌨️ Press <strong>Escape</strong> to close the popup</li>
            <li>📱 Works on both <strong>desktop and mobile</strong> devices</li>
          </ul>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1.5rem',
          margin: '2rem 0'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '8px',
            padding: '1.5rem',
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)'
          }}>
            <h3 style={{
              fontSize: '1.2rem',
              marginBottom: '0.5rem',
              color: '#000000'
            }}>
              🎯 Draggable Icon
            </h3>
            <p style={{
              fontSize: '0.9rem',
              lineHeight: '1.5',
              color: '#242424'
            }}>
              The chat icon can be dragged to any position on the page and stays within viewport bounds.
            </p>
          </div>

          <div style={{
            background: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '8px',
            padding: '1.5rem',
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)'
          }}>
            <h3 style={{
              fontSize: '1.2rem',
              marginBottom: '0.5rem',
              color: '#000000'
            }}>
              📱 Responsive Design
            </h3>
            <p style={{
              fontSize: '0.9rem',
              lineHeight: '1.5',
              color: '#242424'
            }}>
              Popup adapts to different screen sizes and works seamlessly on mobile devices.
            </p>
          </div>

          <div style={{
            background: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '8px',
            padding: '1.5rem',
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)'
          }}>
            <h3 style={{
              fontSize: '1.2rem',
              marginBottom: '0.5rem',
              color: '#000000'
            }}>
              🔒 Secure Iframe
            </h3>
            <p style={{
              fontSize: '0.9rem',
              lineHeight: '1.5',
              color: '#242424'
            }}>
              Chat content loads in a secure iframe with proper sandbox attributes.
            </p>
          </div>

          <div style={{
            background: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '8px',
            padding: '1.5rem',
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)'
          }}>
            <h3 style={{
              fontSize: '1.2rem',
              marginBottom: '0.5rem',
              color: '#000000'
            }}>
              🎨 Customizable
            </h3>
            <p style={{
              fontSize: '0.9rem',
              lineHeight: '1.5',
              color: '#242424'
            }}>
              Easy to customize colors, sizes, and positioning to match your brand.
            </p>
          </div>
        </div>

        <div style={{
          margin: '2rem 0'
        }}>
          <h2 style={{ color: '#000000' }}>🎮 Demo Controls</h2>
          <button
            style={{
              background: 'linear-gradient(135deg, #ffcd00 0%, #ffe45e 100%)',
              color: '#000000',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              margin: '0 10px',
              transition: 'all 0.3s ease',
              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)'
            }}
            onClick={() => (window as any).BlinqxChat?.open()}
          >
            Open Chat
          </button>
          <button
            style={{
              background: 'linear-gradient(135deg, #ffcd00 0%, #ffe45e 100%)',
              color: '#000000',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              margin: '0 10px',
              transition: 'all 0.3s ease',
              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)'
            }}
            onClick={() => (window as any).BlinqxChat?.close()}
          >
            Close Chat
          </button>
          <button
            style={{
              background: 'linear-gradient(135deg, #ffcd00 0%, #ffe45e 100%)',
              color: '#000000',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              margin: '0 10px',
              transition: 'all 0.3s ease',
              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)'
            }}
            onClick={() => (window as any).BlinqxChat?.toggle()}
          >
            Toggle Chat
          </button>
        </div>

        <div style={{
          background: 'rgba(255, 255, 255, 0.9)',
          borderRadius: '8px',
          padding: '2rem',
          margin: '2rem 0',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)'
        }}>
          <h2 style={{ color: '#000000' }}>🔧 Implementation</h2>
          <p style={{ marginBottom: '1rem', color: '#242424' }}>
            To embed this chat on your website, simply include the JavaScript file:
          </p>
          <div style={{
            background: 'rgba(0, 0, 0, 0.1)',
            padding: '1rem',
            borderRadius: '8px',
            fontFamily: 'monospace',
            textAlign: 'left',
            fontSize: '0.9rem',
            color: '#000000'
          }}>
            &lt;script src="chat-embed.js"&gt;&lt;/script&gt;
          </div>
          <p style={{ marginTop: '1rem', color: '#242424' }}>
            The chat icon will automatically appear and be ready to use!
          </p>
        </div>

        <div style={{
          marginTop: '3rem',
          fontSize: '0.9rem',
          color: '#242424'
        }}>
          <p>💡 This is a demonstration of the Quinn as embedded solution</p>
          <p>The chat connects to the /#/chat-popup route for full functionality</p>
        </div>
      </div>

      {/* Chat icon hint */}
      <div
        id="chat-hint"
        style={{
          position: 'fixed',
          bottom: '90px',
          right: '30px',
          background: 'rgba(255, 255, 255, 0.9)',
          color: '#333',
          padding: '8px 12px',
          borderRadius: '8px',
          fontSize: '0.8rem',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
          animation: 'bounce 2s infinite',
          pointerEvents: 'none',
          zIndex: 9998
        }}
      >
        👆 Try me!
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% {
            transform: translateY(0);
          }
          40% {
            transform: translateY(-10px);
          }
          60% {
            transform: translateY(-5px);
          }
        }

        @media (max-width: 768px) {
          h1 {
            font-size: 2rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default EmbedDemo;
